/* aa-nitidez: ninguna imagen se ve pixelada por tamaño, recorte o encuadre.
   - Con srcset (w): ajusta sizes al ancho real que ocupa la foto, incluido el
     recorte de object-fit: cover (una foto 16:9 en una caja alta se dibuja más
     ancha que la caja). Solo sube, nunca baja, para no descargar dos veces.
   - Sin srcset y desde el CDN de Shopify (miniaturas armadas por JS): pide un
     ancho acorde a la pantalla (retina incluida) cambiando el parámetro width.
   El CDN nunca agranda más allá del original, así que no estira fotos chicas.
   Para excluir una imagen: data-aa-nitidez="off". */
(() => {
  if (!('ResizeObserver' in window)) return;
  const CDN = /\/cdn\/shop\/|cdn\.shopify\.com\//;
  const done = new WeakMap();
  const dpr = () => Math.min(window.devicePixelRatio || 1, 3);

  const drawnWidth = (img) => {
    const w = img.clientWidth;
    const h = img.clientHeight;
    if (!w || !h) return 0;
    const iw = img.naturalWidth || +img.getAttribute('width');
    const ih = img.naturalHeight || +img.getAttribute('height');
    if (!iw || !ih) return w;
    const fit = getComputedStyle(img).objectFit;
    if (fit === 'cover') return Math.max(w, (h * iw) / ih);
    if (fit === 'contain' || fit === 'scale-down') return Math.min(w, (h * iw) / ih);
    return w;
  };

  // Ancho de la versión que ya bajó (0 si todavía no empieza a cargar).
  const loadedWidth = (img) => {
    const src = img.currentSrc;
    if (!src) return 0;
    const m = src.match(/[?&]width=(\d+)/);
    return m ? +m[1] : img.naturalWidth || 1;
  };

  // Foto que ya pidió su archivo: solo se cambia si quedó bajo 1,6x;
  // así no se descarga dos veces. Las que aún no cargan usan la densidad real.
  const density = (img, need) => {
    const got = loadedWidth(img);
    if (!got) return dpr();
    const d = Math.min(dpr(), 2);
    return got >= need * d * 0.8 ? 0 : d;
  };

  const withSizes = (img, need) => {
    const d = density(img, need);
    if (!d) return;
    const px = Math.ceil((need * d) / dpr());
    if (px <= (done.get(img) || 0) + 4) return;
    done.set(img, px);
    const pic = img.parentElement && img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;
    if (pic) pic.querySelectorAll('source[srcset]').forEach((s) => { if (/\d+w/.test(s.srcset)) s.sizes = `${px}px`; });
    img.sizes = `${px}px`;
    // Si el srcset no trae una versión lo bastante grande, agrega una del CDN.
    const want = Math.min(Math.ceil((need * d) / 100) * 100, 3840);
    const list = img.srcset.split(',').map((s) => s.trim().split(/\s+/)).filter((c) => /^\d+w$/.test(c[1] || ''));
    const top = list.reduce((a, c) => (parseInt(c[1], 10) > parseInt(a[1], 10) ? c : a), list[0] || ['', '0w']);
    const topW = parseInt(top[1], 10);
    if (!top[0] || want <= topW * 1.05 || !CDN.test(top[0]) || !/[?&]width=\d+/.test(top[0])) return;
    const h = top[0].match(/[?&]height=(\d+)/);
    let url = top[0].replace(/([?&]width=)\d+/, `$1${want}`);
    if (h) url = url.replace(/([?&]height=)\d+/, `$1${Math.round((+h[1] * want) / topW)}`);
    img.srcset += `, ${url} ${want}w`;
  };

  const withCdnWidth = (img, need) => {
    let url;
    try { url = new URL(img.currentSrc || img.src, location.href); } catch (e) { return; }
    const cur = +url.searchParams.get('width');
    if (!cur || /\.svg$/i.test(url.pathname)) return;
    if (img.complete && img.naturalWidth && img.naturalWidth < cur - 1) return; // ya es el original
    const d = density(img, need);
    if (!d) return;
    const want = Math.min(Math.ceil((need * d) / 100) * 100, 3840);
    if (want <= cur * 1.05 || want <= (done.get(img) || 0)) return;
    done.set(img, want);
    const h = +url.searchParams.get('height');
    url.searchParams.set('width', want);
    if (h) url.searchParams.set('height', Math.round((h * want) / cur));
    img.src = url.href;
  };

  const check = (img) => {
    if (img.dataset.aaNitidez === 'off') return;
    const need = drawnWidth(img);
    if (!need) return;
    if (img.srcset && /\d+w/.test(img.srcset)) withSizes(img, need);
    else if (CDN.test(img.getAttribute('src') || '')) withCdnWidth(img, need);
  };

  let queue = new Set();
  let raf = 0;
  const flush = () => { raf = 0; const q = queue; queue = new Set(); q.forEach(check); };
  const schedule = (img) => { queue.add(img); if (!raf) raf = requestAnimationFrame(flush); };

  const ro = new ResizeObserver((entries) => entries.forEach((e) => schedule(e.target)));
  const watch = (img) => {
    if (img._aaNit) return;
    img._aaNit = true;
    ro.observe(img);
    if (!img.complete) img.addEventListener('load', () => schedule(img), { once: true });
  };
  const scan = (root) => {
    if (root.tagName === 'IMG') watch(root);
    else if (root.querySelectorAll) root.querySelectorAll('img').forEach(watch);
  };

  scan(document);
  new MutationObserver((list) => list.forEach((m) => m.addedNodes.forEach(scan)))
    .observe(document.documentElement, { childList: true, subtree: true });
  window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener?.('change', () => {
    document.querySelectorAll('img').forEach(schedule);
  });
})();
